/**
 * useMultiLineInput Hook
 * 
 * Custom hook for handling multi-line input with shift+enter functionality.
 * Provides consistent behavior across input components for better user experience.
 * 
 * Features:
 * - Enter: submits the form/triggers onSubmit callback
 * - Shift+Enter: adds a new line for multi-line input
 * - Auto-resizes textarea based on content with configurable min/max rows
 * - Overflow handling with scrollbars when content exceeds max height
 * - Automatic height adjustment on content changes
 * 
 * Usage:
 * const { textareaRef, handleKeyDown, handleChange } = useMultiLineInput({
 *   onSubmit: () => handleFormSubmit(),
 *   maxRows: 5,
 *   minRows: 1
 * });
 * 
 * Updated: Added multi-line input support for chat and suggestion components
 */

import { useCallback, useRef, useEffect } from 'react';

interface UseMultiLineInputOptions {
  onSubmit: () => void;
  maxRows?: number;
  minRows?: number;
}

export const useMultiLineInput = ({ onSubmit, maxRows = 10, minRows = 1 }: UseMultiLineInputOptions) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  }, [onSubmit]);

  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';

    // Calculate the line height
    const style = window.getComputedStyle(textarea);
    const lineHeight = parseInt(style.lineHeight) || 20;

    // Calculate min and max heights
    const minHeight = lineHeight * minRows;
    const maxHeight = lineHeight * maxRows;

    // Set the height based on content, constrained by min/max
    const scrollHeight = textarea.scrollHeight;
    const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);

    textarea.style.height = `${newHeight}px`;
    textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [maxRows, minRows]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>, originalOnChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void) => {
    if (originalOnChange) {
      originalOnChange(e);
    }
    // Adjust height on next tick to ensure the value has been updated
    setTimeout(adjustHeight, 0);
  }, [adjustHeight]);

  // Adjust height when component mounts or content changes
  useEffect(() => {
    adjustHeight();
  }, [adjustHeight]);

  return {
    textareaRef,
    handleKeyDown,
    handleChange,
    adjustHeight
  };
};
